import React, { useState, useEffect, useMemo } from 'react';
import '../../App.css';
import Select from 'react-select';
import { useNavigate, useLocation } from 'react-router-dom';
import { bookingService, locationService, orderClientService } from '../../api/apiService';

/**
 * The shared "typed order" form behind Create K.I.N.D. Order and Create Copywrite Order.
 *
 * Both flows create one booking per selected date, optionally grouped, with most fields fixed
 * by the order type. What differs is described by `config` (see CreateKindOrder.js and
 * CreateCopywriteOrder.js), so a change to the shared behaviour lands in both.
 *
 * Clients come from the database (Settings → Order clients). A client entry can carry default
 * cost centers; picking it copies them onto the booking, which is how e.g. a Copywrite order
 * gets its Kostenstelle "Copywriter" and the client's Kostenträger without anyone typing them.
 */
export const BILLING_OPTIONS = {
  daily: { value: 'daily', label: 'Day Rate (per day)' },
  hourly: { value: 'hourly', label: 'Hourly Rate (per hour)' },
  kpi: { value: 'kpi', label: 'KPI (price per KPI)' },
};

const selectStyles = {
  dropdownIndicator: (provided) => ({ ...provided, border: 'none' }),
  control: (base) => ({
    ...base,
    width: '100%',
    borderColor: 'black',
    borderWidth: '1px',
    boxShadow: 'none',
    margin: '0 0 10px 0',
    borderRadius: '0px',
    color: 'black',
    fontWeight: '700',
    height: '50px',
    textTransform: 'uppercase',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  placeholder: (provided) => ({ ...provided, color: 'black', fontWeight: '700' }),
  input: (provided) => ({ ...provided, color: 'black', fontWeight: '700' }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? 'black' : 'inherit',
    '&:hover': { backgroundColor: state.isSelected ? 'black' : '#f7f7f7' },
  }),
};

const fixedInputStyle = { backgroundColor: '#f5f5f5', cursor: 'not-allowed' };
const hintStyle = { marginTop: '4px', fontSize: '13px', color: '#666', fontStyle: 'italic' };

/** Case-insensitive match of a location record against the names/short codes a config lists. */
const matchLocation = (records, names) => {
  const wanted = names.map((n) => String(n).toLowerCase());
  return records.find((loc) =>
    wanted.includes(String(loc.name || '').toLowerCase()) || wanted.includes(String(loc.short || '').toLowerCase())
  ) || null;
};

function TypedOrderForm({ config }) {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Clients from the DB (Settings → Order clients). `loadError` keeps the form usable with a
  // clear message instead of an empty dropdown when the backend is not there yet.
  const [clientOptions, setClientOptions] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientsError, setClientsError] = useState(null);
  const [client, setClient] = useState(null);

  const [additionalInfo, setAdditionalInfo] = useState('');
  const [typeInfo, setTypeInfo] = useState(''); // KIND info / Copywrite info → kind_info column
  const [selectedDates, setSelectedDates] = useState([]);
  const [dateInput, setDateInput] = useState('');
  const [createAsGroup, setCreateAsGroup] = useState(true);
  const [kpi, setKpi] = useState('');
  const [hours, setHours] = useState('');

  const billingOptions = useMemo(
    () => config.billing.map((key) => BILLING_OPTIONS[key]).filter(Boolean),
    [config.billing]
  );
  const [selectedBilling, setSelectedBilling] = useState(billingOptions[0]);
  const isHourly = selectedBilling?.value === 'hourly';
  const isKpi = selectedBilling?.value === 'kpi';

  const [selectedPosition, setSelectedPosition] = useState(
    config.positions.find((p) => p.value === config.defaultPosition) || config.positions[0] || null
  );


  // Locations: the form shows the configured labels; on submit the matching API record (full
  // object) is sent so the booking gets a proper location_id.
  const [selectedLocation, setSelectedLocation] = useState(config.locations[0] || null);
  const [locationRecords, setLocationRecords] = useState({}); // keyed by config location value
  const [locationsLoaded, setLocationsLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await locationService.getAllLocations(1, 1000);
        const records = res.data?.locations || [];
        const map = {};
        config.locations.forEach((loc) => {
          map[loc.value] = matchLocation(records, [loc.label, ...(loc.match || [])]);
        });
        if (!cancelled) setLocationRecords(map);
      } catch (err) {
        console.error(`Error fetching locations for ${config.orderType} orders:`, err);
      } finally {
        if (!cancelled) setLocationsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [config.locations, config.orderType]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await orderClientService.getAll({ type: config.orderType });
        const clients = res.data?.clients || [];
        if (!cancelled) {
          setClientOptions(clients.map((c) => ({
            value: c.value,
            label: c.label,
            defaultCostCenter1: c.defaultCostCenter1 || null,
            defaultCostCenter2: c.defaultCostCenter2 || null,
          })));
          setClientsError(null);
        }
      } catch (err) {
        console.error(`Error loading ${config.orderType} clients:`, err);
        if (!cancelled) setClientsError('Client list could not be loaded. Check Settings → Order clients, or try again.');
      } finally {
        if (!cancelled) setClientsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [config.orderType]);

  const handleAddDate = () => {
    if (!dateInput) return;
    if (selectedDates.includes(dateInput)) {
      alert('This date is already selected');
      return;
    }
    setSelectedDates([...selectedDates, dateInput].sort());
    setDateInput('');
  };

  const handleRemoveDate = (dateToRemove) => {
    setSelectedDates(selectedDates.filter((d) => d !== dateToRemove));
  };

  const formatDate = (dateString) =>
    new Date(dateString + 'T00:00:00').toLocaleDateString('de-DE', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });

  /** "kind-42": from the backend; falls back to the old client-side scan on an older backend. */
  const nextGroupId = async () => {
    try {
      const res = await bookingService.getNextGroupId(config.groupPrefix);
      if (res.data?.groupId) return res.data.groupId;
    } catch (err) {
      console.warn('next-group-id endpoint unavailable, scanning bookings instead:', err?.message || err);
    }
    const res = await bookingService.getAllBookings(1, 10000);
    const re = new RegExp(`^${config.groupPrefix}-(\\d+)$`);
    const max = (res.data?.bookings || [])
      .map((b) => (b.group && b.group.match(re) ? parseInt(b.group.match(re)[1], 10) : 0))
      .reduce((m, n) => (n > m ? n : m), 0);
    return `${config.groupPrefix}-${max + 1}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!client) return alert('Please select a client');
    if (selectedDates.length === 0) return alert('Please select at least one date');
    if (!selectedPosition) return alert('Please select a position');
    if (!selectedLocation) return alert('Please select a location');
    if (isHourly && !(parseFloat(hours) > 0)) return alert('Please enter the amount of hours (greater than 0) for hourly billing');
    if (isKpi && !(parseInt(kpi, 10) > 0)) return alert('Please enter the number of KPIs (greater than 0) for KPI-based billing');

    const locationRecord = locationRecords[selectedLocation.value] || null;
    if (config.fixedLocation && !locationRecord) {
      // Decision D6: the fixed location must exist as a real record — no silent fallback to a
      // label string, otherwise the booking would have no location_id.
      return alert(`Location "${selectedLocation.label}" is not set up in TOBS yet. Add it under Locations first.`);
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const numberOfDays = selectedDates.length;
      const groupId = createAsGroup && numberOfDays > 1 ? await nextGroupId() : null;
      const locationForBooking = locationRecord || selectedLocation.label;

      const payloads = selectedDates.map((date) => ({
        title: `${config.noun} - ${numberOfDays} day${numberOfDays > 1 ? 's' : ''}`,
        start_date: date,
        end_date: date,
        status: 'new order',
        order_type: config.orderType,

        client: { label: client.label, value: client.value },
        position: { label: selectedPosition.label, value: selectedPosition.value },

        studio: config.studio,
        location: locationForBooking,
        set: config.set,

        // Default cost centers of the chosen client (Settings → Order clients). Still editable
        // later in Propose Talent, where they are mandatory anyway.
        ...(client.defaultCostCenter1 && { cost_center1: client.defaultCostCenter1 }),
        ...(client.defaultCostCenter2 && { cost_center2: client.defaultCostCenter2 }),

        kpi: isKpi ? parseInt(kpi, 10) : (kpi.trim() || 0),
        sonstige_info: additionalInfo || '',
        kind_info: typeInfo.trim() || null,

        created_by: localStorage.getItem('userEmail') || '',
        start_time: '09:00',
        end_time: '18:00',

        billing_type: selectedBilling?.value || 'daily',
        ...(isHourly && { booked_hours: parseFloat(hours) }),
        ...(groupId && { group: groupId }),
      }));

      const responses = await Promise.all(payloads.map((p) => bookingService.createBooking(p)));

      if (responses.every((r) => r && r.data && r.data.booking)) {
        alert(`${numberOfDays} ${config.noun}${numberOfDays > 1 ? 's' : ''} created successfully${groupId ? ' and grouped together' : ''}!`);
        navigate(routerLocation.pathname.startsWith('/cs') ? '/cs/allbooking' : '/admin/newallbookings');
      } else {
        throw new Error('Some orders failed to create');
      }
    } catch (err) {
      console.error(`Error creating ${config.noun}s:`, err);
      const msg = err.response?.data?.message || err.message;
      setError(selectedDates.length > 1
        ? `Error creating ${selectedDates.length} ${config.noun}s: ${msg}`
        : `Error creating ${config.noun}: ${msg}`);
      alert(`Failed to create ${config.noun}(s): ${msg}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const dateInputId = `${config.orderType}-date-input`;
  const defaultsHint = client && (client.defaultCostCenter1 || client.defaultCostCenter2)
    ? [client.defaultCostCenter1, client.defaultCostCenter2]
        .filter(Boolean)
        .map((c) => `${c.label} (${c.cnAccountNumber || c.accountNumber})`)
        .join(' · ')
    : null;

  return (
    <div className="content-wrapper page">
      <section>
        <h1 className="h1">{config.title}</h1>
        {error && <div style={{ color: 'red', marginBottom: '20px', padding: '15px', backgroundColor: '#ffebee' }}>{error}</div>}

        <form onSubmit={handleSubmit} style={{ marginTop: '40px', maxWidth: '500px' }}>
          <Select
            options={clientOptions}
            value={client}
            onChange={setClient}
            placeholder={clientsLoading ? 'Loading clients…' : 'Client'}
            styles={selectStyles}
            isClearable
            isLoading={clientsLoading}
            noOptionsMessage={() => (clientsError ? clientsError : 'No clients configured for this order type')}
          />
          {clientsError && <div style={{ ...hintStyle, color: '#b00020', marginTop: '-6px', marginBottom: '10px' }}>{clientsError}</div>}
          {defaultsHint && (
            <div style={{ ...hintStyle, marginTop: '-6px', marginBottom: '10px' }}>
              Cost centers will be set: {defaultsHint}
            </div>
          )}

          {config.fixedPosition ? (
            <input className="inputtext" type="text" value={selectedPosition?.label || ''} disabled placeholder="Position" style={fixedInputStyle} />
          ) : (
            <Select
              options={config.positions}
              value={selectedPosition}
              onChange={setSelectedPosition}
              placeholder="Position"
              styles={selectStyles}
              isClearable={false}
            />
          )}

          <input className="inputtext" type="text" value={config.studio} disabled placeholder="Studio" style={fixedInputStyle} />

          {config.fixedLocation ? (
            <input
              className="inputtext"
              type="text"
              value={selectedLocation?.label || ''}
              disabled
              placeholder="Location"
              style={fixedInputStyle}
              title={locationsLoaded && !locationRecords[selectedLocation?.value] ? 'This location is not set up in TOBS yet' : undefined}
            />
          ) : (
            <Select
              options={config.locations}
              value={selectedLocation}
              onChange={setSelectedLocation}
              placeholder="Location"
              styles={selectStyles}
              isClearable={false}
            />
          )}
          {config.fixedLocation && locationsLoaded && selectedLocation && !locationRecords[selectedLocation.value] && (
            <div style={{ ...hintStyle, color: '#b00020', marginTop: '-6px', marginBottom: '10px' }}>
              Location "{selectedLocation.label}" does not exist in TOBS yet — add it under Locations before creating orders.
            </div>
          )}

          <input className="inputtext" type="text" value={config.set} disabled placeholder="Set" style={fixedInputStyle} />

          <Select
            options={billingOptions}
            value={selectedBilling}
            onChange={(opt) => setSelectedBilling(opt)}
            placeholder="Billing"
            styles={selectStyles}
            isClearable={false}
          />

          {isHourly && (
            <div style={{ marginBottom: '10px' }}>
              <label style={{ fontWeight: '700', marginBottom: '5px', display: 'block' }}>Amount of hours</label>
              <input className="inputtext" type="number" min="0" step="0.25" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="e.g. 3.5" style={{ margin: 0 }} />
              <div style={hintStyle}>Billed per selected date. Fractions allowed (e.g. 1.5, 2.25).</div>
            </div>
          )}

          {isKpi ? (
            <div style={{ marginBottom: '10px' }}>
              <label style={{ fontWeight: '700', marginBottom: '5px', display: 'block' }}>KPI (required)</label>
              <input className="inputtext" type="number" min="1" step="1" value={kpi} onChange={(e) => setKpi(e.target.value)} placeholder="e.g. 40" style={{ margin: 0 }} required />
              <div style={hintStyle}>Number of KPIs for this order — billed at the rate per KPI set when proposing a talent.</div>
            </div>
          ) : (
            <input className="inputtext" type="text" value={kpi} onChange={(e) => setKpi(e.target.value)} placeholder="KPI (optional)" />
          )}

          <div style={{ marginTop: '10px' }}>
            <label style={{ fontWeight: '700', marginBottom: '5px', display: 'block' }}>Select Dates</label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              <div
                style={{ position: 'relative', flex: '1', display: 'flex', alignItems: 'center', border: '1px solid #000', backgroundColor: '#fff', padding: '10px', cursor: 'pointer', height: '50px' }}
                onClick={() => document.getElementById(dateInputId)?.showPicker?.()}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '10px', flexShrink: 0 }}>
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                <input
                  id={dateInputId}
                  className="inputdate"
                  type="date"
                  value={dateInput}
                  onChange={(e) => setDateInput(e.target.value)}
                  style={{ flex: '1', border: 'none', outline: 'none', padding: '0', margin: '0', backgroundColor: 'transparent', cursor: 'pointer', fontSize: '14px' }}
                />
              </div>
              <button type="button" onClick={handleAddDate} className="big-button" style={{ padding: '10px 20px', height: '50px', margin: 0 }}>
                Add Date
              </button>
            </div>
          </div>

          {selectedDates.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <label style={{ fontWeight: '700', marginBottom: '10px', display: 'block' }}>
                Selected Dates ({selectedDates.length} day{selectedDates.length > 1 ? 's' : ''})
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', maxHeight: '200px', overflowY: 'auto', padding: '10px', backgroundColor: '#f9f9f9' }}>
                {selectedDates.map((date) => (
                  <div key={date} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', backgroundColor: 'white', border: '1px solid #ddd', fontSize: '14px' }}>
                    <span style={{ fontWeight: '600' }}>{formatDate(date)}</span>
                    <button type="button" onClick={() => handleRemoveDate(date)} style={{ background: 'none', border: 'none', color: '#ff4444', cursor: 'pointer', fontSize: '18px', padding: '0 4px', lineHeight: '1' }} title="Remove date">
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <textarea className="inputtext" value={additionalInfo} onChange={(e) => setAdditionalInfo(e.target.value)} style={{ height: '100px', marginTop: '10px' }} placeholder="Additional Information" />

          <div style={{ marginTop: '20px' }}>
            <label style={{ fontWeight: '700', marginBottom: '5px', display: 'block' }}>{config.infoLabel}</label>
            <textarea className="inputtext" value={typeInfo} onChange={(e) => setTypeInfo(e.target.value)} style={{ height: '80px', marginTop: '5px' }} placeholder={config.infoPlaceholder} />
          </div>

          {selectedDates.length > 1 && (
            <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#f9f9f9', border: '1px solid #ddd' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '15px', fontWeight: '600' }}>
                <input type="checkbox" checked={createAsGroup} onChange={(e) => setCreateAsGroup(e.target.checked)} style={{ width: '20px', height: '20px', marginRight: '12px', cursor: 'pointer' }} />
                <span>Group these {selectedDates.length} orders together</span>
              </label>
              <div style={{ marginTop: '8px', marginLeft: '32px', fontSize: '13px', color: '#666', fontStyle: 'italic' }}>
                All orders will be linked and can be managed as a group
              </div>
            </div>
          )}

          <div style={{ marginTop: '40px' }}>
            <button
              type="submit"
              className="big-button-active"
              disabled={isSubmitting || clientsLoading}
              style={{ padding: '15px 50px', fontSize: '16px', fontWeight: '700', minWidth: '250px', backgroundColor: '#000', color: '#fff', border: 'none', cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
            >
              {isSubmitting ? 'Creating...' : config.buttonLabel}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

export default TypedOrderForm;
